/*
 * ==========================================================================
 * SingRank - Lead + CTA click capture                           v1.1.1
 * --------------------------------------------------------------------------
 * WHY THIS EXISTS
 * A site can only be judged on the actions people actually take on it. Before
 * this file, ablink recorded WhatsApp opens and nothing else - measured
 * 2026-09-01, 549 leads were 548 WhatsApp and exactly 1 form. Contact forms,
 * phone taps, email taps and outbound CTA clicks were all invisible.
 *
 * WHAT IT CAPTURES
 *   1. Contact form submits   name / phone / email / website / message
 *   2. Phone taps             tel: links
 *   3. Email taps             mailto: links
 *   4. Outbound CTA clicks    any link to a host in OUTBOUND_HOSTS below
 *
 * Every one of those carries first-touch attribution (first_page,
 * first_referrer, first_ts) and the last Google Ads click id, so a lead can be
 * credited to the page that BROUGHT the visitor, not just the page they were
 * on when they finally acted.
 *
 * WHY OUTBOUND CTA CLICKS MATTER
 * On RCS the "Ask Aideal lah!" buttons point at ai-deal.ai - 2 on the
 * homepage, 6 on a typical article, 2 on the contact page - and a click on
 * them left no trace on either side. ai-deal.ai is not served from our NAS, so
 * there were no server logs to fall back on either. Those clicks were simply
 * never counted anywhere.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   WhatsApp. On ablink sections/ablink-wa-widget.liquid already captures every
 *   WA open (click listener + window.open wrapper); on RCS the existing tracker
 *   does. Listening here as well would write two rows per click - the exact
 *   fault found on RCS on 2026-09-01. WhatsApp links are skipped explicitly.
 *   The GEO / AI pixel is likewise owned by assets/singrank-geo-pixel.js.
 *
 * SLUG IS "ablink.sg", NOT "ablink"
 * Deliberate. The WhatsApp widget derives its slug from location.hostname and
 * writes "ablink.sg"; all historical leads sit in that bucket. Sending
 * "ablink" from here would split one client across two buckets. The server's
 * CLIENT_ALIASES map folds ablink / ablink.sg / www.ablink.sg together, so the
 * split is cosmetic - but matching the existing bucket keeps it honest.
 * PORTING TO ANOTHER CLIENT: set SLUG explicitly if the hostname does not
 * equal the dashboard bucket. RCS must be hard-coded to "rcs", because the
 * hostname would otherwise send "renovationcontractorsingapore.com".
 *
 * FIELD NAMES MATTER
 * The server stores exactly: client, name, phone, email, website, message,
 * wa_number, page, title, referrer, gclid, first_page, first_referrer,
 * first_ts. Anything else is silently dropped.
 *
 * CONTENT TYPE IS text/plain AND MUST STAY THAT WAY
 * application/json triggers a CORS preflight that sendBeacon cannot perform,
 * so the lead dies silently. The server reads the raw body regardless.
 *
 * SAFETY
 * - Never blocks, delays or rewrites a click or a submit. Fire and forget.
 * - Skips search boxes and newsletter-only forms (no email, phone or message).
 * - One row per click: a short same-target debounce absorbs the duplicate
 *   events that fire when a button sits inside another clickable element.
 * - Guarded by window.__SR_LEAD_FORM__ so a second copy is a no-op.
 * - try/catch throughout; on failure the page behaves exactly as before.
 * - No regular expressions and no escape sequences, by house rule.
 *
 * DEBUG
 *   window.SR_LEAD_FORM.state()  slug, first-touch and last payload sent
 *
 * Code by SingRank (https://singrank.com) for AB Link Pte Ltd. Created 2026-09-01.
 * ==========================================================================
 */
(function () {
  'use strict';

  if (window.__SR_LEAD_FORM__) { return; }
  window.__SR_LEAD_FORM__ = true;

  var ENDPOINT = 'https://leads.rumah.live/track';

  var host = String(window.location.hostname || '').toLowerCase();
  if (host.indexOf('www.') === 0) { host = host.substring(4); }
  var SLUG = host || 'ablink.sg';

  // Outbound destinations worth counting as an intent signal. Add a host here
  // and its clicks start being recorded; nothing else about the page changes.
  var OUTBOUND_HOSTS = ['ai-deal.ai', 'aideal.ai'];

  // Never captured here - another tracker already owns them.
  var WHATSAPP_MARKERS = ['wa.me', 'whatsapp.com', 'whatsapp:'];

  var STATE = { slug: SLUG, first: null, gclid: '', lastSent: null, count: 0 };
  var lastClick = { key: '', at: 0 };

  function low(s) { return String(s == null ? '' : s).toLowerCase(); }

  function contains(haystack, needles) {
    var h = low(haystack);
    if (!h) { return false; }
    for (var i = 0; i < needles.length; i++) {
      if (h.indexOf(needles[i]) > -1) { return true; }
    }
    return false;
  }

  function param(name) {
    try {
      var q = window.location.search || '';
      if (!q) { return ''; }
      var parts = q.substring(1).split('&');
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq < 0) { continue; }
        if (decodeURIComponent(parts[i].substring(0, eq)).toLowerCase() === name) {
          return decodeURIComponent(parts[i].substring(eq + 1).split('+').join(' '));
        }
      }
    } catch (e) {}
    return '';
  }

  // First touch. Prefer the record the WhatsApp widget already keeps under
  // "abl_ft" so both halves of the site agree on where the visitor came from;
  // fall back to our own key on a browser that has only ever seen this file.
  function firstTouch() {
    try {
      var raw = window.localStorage.getItem('abl_ft');
      if (raw) {
        var a = JSON.parse(raw);
        if (a && a.landing_url) {
          return { p: a.landing_url, r: a.landing_referrer || '', t: a.ts || '' };
        }
      }
    } catch (e) {}

    try {
      var key = '__sr_ft_' + SLUG;
      var mine = window.localStorage.getItem(key);
      if (mine) {
        var m = JSON.parse(mine);
        if (m && m.p) { return m; }
      }
      var fresh = {
        p: window.location.href,
        r: document.referrer || '',
        t: new Date().toISOString()
      };
      window.localStorage.setItem(key, JSON.stringify(fresh));
      return fresh;
    } catch (e) {}

    return { p: window.location.href, r: document.referrer || '', t: '' };
  }

  // Last Google Ads click wins, and it has to survive the pages in between.
  function adClickId() {
    try {
      var key = '__sr_g_' + SLUG;
      var found = param('gclid') || param('wbraid') || param('gbraid');
      if (found) { window.localStorage.setItem(key, found); return found; }
      return window.localStorage.getItem(key) || '';
    } catch (e) {}
    return param('gclid') || '';
  }

  STATE.first = firstTouch();
  STATE.gclid = adClickId();

  function send(payload) {
    try {
      payload.client   = SLUG;
      payload.page     = window.location.href;
      payload.title    = document.title || '';
      payload.referrer = document.referrer || '';
      payload.ts       = new Date().toISOString();

      if (STATE.gclid) { payload.gclid = STATE.gclid; }
      if (STATE.first && STATE.first.p) {
        payload.first_page     = STATE.first.p;
        payload.first_referrer = STATE.first.r || '';
        payload.first_ts       = STATE.first.t || '';
      }

      var body = JSON.stringify(payload);
      var blob = new Blob([body], { type: 'text/plain' });

      STATE.lastSent = payload;
      STATE.count = STATE.count + 1;

      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) { return true; }

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: body,
        keepalive: true,
        mode: 'no-cors'
      });
      return true;
    } catch (e) { return false; }
  }

  function read(form, selector) {
    try {
      var el = form.querySelector(selector);
      return el ? String(el.value || '').trim() : '';
    } catch (e) { return ''; }
  }

  // ---------- 1) contact form submits ----------
  document.addEventListener('submit', function (ev) {
    try {
      var f = ev.target;
      if (!f || f.tagName !== 'FORM') { return; }

      var email = read(f, '[type="email"],[name*="email" i],[name="contact[email]"]');
      var phone = read(f, '[type="tel"],[name*="phone" i],[name*="tel" i]');
      var msg   = read(f, 'textarea,[name*="message" i],[name*="body" i],[name="contact[body]"]');
      var name  = read(f, '[name*="name" i],[name="contact[name]"],[autocomplete="name"]');
      var web   = read(f, '[type="url"],[name*="website" i],[autocomplete="url"]');

      // A search box or a bare newsletter signup is not an enquiry.
      if (!email && !phone && !msg) { return; }

      send({
        name: name,
        phone: phone,
        email: email,
        website: web,
        message: msg || 'Form submit'
      });
    } catch (e) {}
  }, true);

  // ---------- 2,3,4) phone / email / outbound CTA clicks ----------
  function linkHost(href) {
    try { return low(new URL(href, window.location.href).hostname); }
    catch (e) { return ''; }
  }

  function once(key) {
    var now = new Date().getTime();
    if (key === lastClick.key && (now - lastClick.at) < 1200) { return false; }
    lastClick.key = key;
    lastClick.at = now;
    return true;
  }

  document.addEventListener('click', function (ev) {
    try {
      var a = (ev.target && ev.target.closest) ? ev.target.closest('a[href]') : null;
      if (!a) { return; }

      var href = a.getAttribute('href') || '';
      if (!href) { return; }
      var h = low(href);

      // WhatsApp belongs to the other tracker. Never double-count it.
      if (contains(h, WHATSAPP_MARKERS)) { return; }

      var label = String(a.textContent || '').replace('\n', ' ').trim().substring(0, 60);

      if (h.indexOf('tel:') === 0) {
        if (!once('tel|' + h)) { return; }
        send({ phone: href.substring(4).trim(), message: 'Phone click' });
        return;
      }

      if (h.indexOf('mailto:') === 0) {
        if (!once('mail|' + h)) { return; }
        send({ email: href.substring(7).split('?')[0].trim(), message: 'Email click' });
        return;
      }

      var target = linkHost(href);
      if (!target) { return; }
      for (var i = 0; i < OUTBOUND_HOSTS.length; i++) {
        if (target.indexOf(OUTBOUND_HOSTS[i]) > -1) {
          if (!once('out|' + h)) { return; }
          send({
            website: href.substring(0, 290),
            message: 'Outbound click: ' + OUTBOUND_HOSTS[i] + (label ? ' — ' + label : '')
          });
          return;
        }
      }
    } catch (e) {}
  }, true);

  window.SR_LEAD_FORM = {
    version: '1.1.1',
    slug: SLUG,
    outbound: OUTBOUND_HOSTS,
    state: function () { return STATE; }
  };
})();
